


















































import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import ScrollService from '~/services/scroll/ScrollService'
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  reactive,
  ref,
  toRefs,
  watch
} from '@nuxtjs/composition-api'
import { useI18n } from '~/compositions/i18n'
import { useDeps } from '~/compositions/dependency-container'
import { useNamespacedStore } from '~/compositions/store'
import {
  USER_AGENT_NS,
  UserAgentState
} from '~/store/modules/shared/userAgent/state'
import { ButtonVariant } from '~/models/app/button'

export default defineComponent({
  props: {
    threshold: {
      type: Number,
      default: 340
    },
    scrollOnExpand: {
      type: Boolean,
      default: true
    },
    preExpanded: {
      type: Boolean,
      default: false
    },
    inheritBodyBg: {
      type: Boolean,
      default: false
    },
    whiteBackground: {
      type: Boolean,
      default: false
    },
    whiteSpacePreWrap: {
      type: Boolean,
      default: true
    },
    controlContainerClass: {
      type: [String, Array, Object],
      default: () => []
    },
    containerOpacity: {
      type: Number,
      default: 0
    },
    variant: {
      type: String as PropType<ButtonVariant>,
      default: 'primary'
    },
    arrowOnly: {
      type: Boolean,
      default: false
    },
    contentContainerClasses: {
      type: [String, Array, Object],
      default: () => []
    },
    staticButton: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const {
      threshold,
      scrollOnExpand,
      inheritBodyBg,
      preExpanded,
      containerOpacity,
      staticButton
    } = toRefs(props)

    const { getters: userAgentGetters } = useNamespacedStore<UserAgentState>(
      USER_AGENT_NS
    )
    const isMobile = computed(() => userAgentGetters('isMobile'))
    const contentContainerTemplateRef = ref<HTMLElement | null>()
    const wrapperTemplateRef = ref<HTMLElement | null>()
    const expandControlContainerTemplateRef = ref<HTMLElement | null>()

    const scrollHeight = ref(600)
    const showExpand = ref(false)
    const expanded = ref(false)
    const hiddenHeight = ref(threshold.value)
    const contentContainerStyle = reactive({
      maxHeight: hiddenHeight.value + 'px',
      paddingBottom: null
    })

    watch(threshold, async newVal => {
      hiddenHeight.value = newVal
      contentContainerStyle.maxHeight = hiddenHeight.value + 'px'
      contentContainerStyle.paddingBottom = null
      await nextTick()
      checkAndMakeExpandable()
    })

    const { t } = useI18n()
    const [scrollService] = useDeps(ScrollService)

    const expandButtonIcon = computed(() =>
      expanded.value ? faChevronUp : faChevronDown
    )
    const expandButtonText = computed(() =>
      expanded.value ? t('less') : t('more')
    )
    const expandControlContainerClasses = computed(() => {
      if (!showExpand.value) {
        return ['tw-hidden']
      }

      if (expanded.value) {
        return ['tw-pt-5']
      }
      return ['tw-pt-10', 'tw-pb-5']
    })

    function toggleExpand() {
      if (!contentContainerTemplateRef.value || !wrapperTemplateRef.value) {
        return
      }
      scrollHeight.value = contentContainerTemplateRef.value.scrollHeight
      expanded.value = !expanded.value
      emit('expand', expanded.value)
      if (scrollOnExpand.value) {
        scrollService.scrollTo(wrapperTemplateRef.value, {
          easing: 'ease-out',
          offset: -60,
          duration: 500
        })
      }
    }

    function checkAndMakeExpandable() {
      if (
        !expandControlContainerTemplateRef.value ||
        !contentContainerTemplateRef.value
      ) {
        return
      }
      const containerBackgroundColor = getComputedStyle(
        inheritBodyBg.value
          ? document.body
          : wrapperTemplateRef.value ?? document.body,
        null
      )['background-color']

      expandControlContainerTemplateRef.value.style.backgroundImage = `linear-gradient(to bottom, rgba(255, 255, 255, ${
        containerOpacity.value
      }), ${
        containerBackgroundColor === 'rgba(0, 0, 0, 0)'
          ? 'white'
          : containerBackgroundColor
      })`

      scrollHeight.value = contentContainerTemplateRef.value.scrollHeight
      if (scrollHeight.value > threshold.value) {
        hiddenHeight.value = threshold.value
        showExpand.value = true
      } else {
        showExpand.value = false
      }
    }

    watch(expanded, async newExpanded => {
      if (
        !contentContainerTemplateRef.value ||
        !expandControlContainerTemplateRef.value
      ) {
        return
      }
      await nextTick()
      const controlContainerHeight =
        expandControlContainerTemplateRef.value?.offsetHeight
      if (newExpanded) {
        if (!staticButton.value) {
          contentContainerStyle.paddingBottom = `${controlContainerHeight}px`
        }
        contentContainerStyle.maxHeight = `${scrollHeight.value +
          controlContainerHeight}px`
      } else {
        contentContainerStyle.paddingBottom = null
        contentContainerStyle.maxHeight = `${hiddenHeight.value}px`
      }
    })

    onMounted(() => {
      emit('mounted')
      checkAndMakeExpandable()
      if (preExpanded.value) {
        toggleExpand()
      }
    })

    return {
      scrollHeight,
      showExpand,
      expanded,
      hiddenHeight,
      expandButtonIcon,
      expandButtonText,
      expandControlContainerClasses,
      contentContainerStyle,
      contentContainerTemplateRef,
      expandControlContainerTemplateRef,
      wrapperTemplateRef,
      isMobile,
      toggleExpand,
      checkAndMakeExpandable
    }
  }
})
